import React from 'react';
import { MapPin, BookOpen, Clock, Users, Eye, Shield, GraduationCap, User, Monitor, Home, Building, Calendar, Target, MousePointerClick } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';

// Helper functions
export const formatBudget = (type, amount) => {
    return `₹${amount}${type === 'hourly' ? '/hr' : '/month'}`;
};

export const getModeIcon = (mode) => {
    switch (mode) {
        case 'online': return <Monitor className="h-4 w-4" />;
        case 'offline_tutor_place': return <Building className="h-4 w-4" />;
        case 'offline_student_place': return <Home className="h-4 w-4" />;
        default: return <MapPin className="h-4 w-4" />;
    }
};

export const getModeLabel = (mode) => {
    switch (mode) {
        case 'online': return 'Online';
        case 'offline_tutor_place': return "At Tutor's Place";
        case 'offline_student_place': return "At Student's Place";
        case 'nearby_classes': return 'Nearby Classes';
        default: return mode;
    }
};

export const getStatusColor = (status) => {
    switch (status) {
        case 'open': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
        case 'closed': return 'bg-red-100 text-red-700 border-red-200';
        default: return 'bg-blue-100 text-blue-700 border-blue-200';
    }
};

export const TuitionPostCard = ({ post, onViewDetails }) => (
    <Card
        className="group hover:shadow-xl transition-all duration-300 border-0 bg-white overflow-hidden cursor-pointer"
        onClick={() => onViewDetails(post.id)}
        data-testid={`tuition-card-${post.id}`}
    >
        <CardContent className="p-0">
            {/* Gradient accent bar */}
            <div className="h-1.5 bg-gradient-to-r from-blue-500 to-[#041f96]" />

            <div className="p-6">
                {/* Header Section */}
                <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                            <h3 className="text-xl font-bold text-gray-900 group-hover:text-emerald-600 transition-colors">
                                {post.category}
                            </h3>
                            <Badge className={getStatusColor(post.status)}>
                                {post.status}
                            </Badge>
                            {post.posted_by_admin && (
                                <Badge className="bg-gradient-to-r from-blue-500 to-[#041f96] text-white border-0">
                                    <Shield className="h-3 w-3 mr-1" />
                                    Admin
                                </Badge>
                            )}
                        </div>
                        <p className="text-gray-600 line-clamp-2">{post.description}</p>
                    </div>

                    <div className="ml-4 text-right">
                        <div className="text-2xl font-bold bg-gradient-to-r from-gray-600 to-gray-600 bg-clip-text text-transparent">
                            {formatBudget(post.budget_type, post.budget_amount)}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                            {post.budget_type === 'hourly' ? 'per hour' : 'per month'}
                        </div>
                    </div>
                </div>

                {/* Info Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                            <BookOpen className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                            <div className="text-xs text-gray-500">Board</div>
                            <div className="font-semibold text-gray-900">{post.board?.toUpperCase()}</div>
                        </div>
                    </div>

                    <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                            <MapPin className="h-5 w-5 text-emerald-600" />
                        </div>
                        <div>
                            <div className="text-xs text-gray-500">Location</div>
                            <div className="font-semibold text-gray-900">{post.city || 'Not specified'}</div>
                            {post.distance_display && post.distance_display !== null && (
                                <div className="text-xs text-blue-600 font-medium">• {post.distance_display} away</div>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                            {getModeIcon(post.class_mode)}
                        </div>
                        <div>
                            <div className="text-xs text-gray-500">Mode</div>
                            <div className="font-semibold text-gray-900">{getModeLabel(post.class_mode)}</div>
                        </div>
                    </div>

                    <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                            <Clock className="h-5 w-5 text-amber-600" />
                        </div>
                        <div>
                            <div className="text-xs text-gray-500">Posted</div>
                            <div className="font-semibold text-gray-900">{post.posted_ago || 'Recently'}</div>
                        </div>
                    </div>
                </div>

                {/* Subjects */}
                <div className="flex flex-wrap gap-2 mb-4">
                    {post.subjects?.map((subject, index) => (
                        <Badge key={index} variant="secondary" className="bg-emerald-50 text-emerald-700 border-emerald-100 px-3 py-1">
                            <GraduationCap className="h-3 w-3 mr-1" />
                            {subject}
                        </Badge>
                    ))}
                    {post.availability && (
                        <Badge variant="outline" className="border-gray-200 text-gray-600 px-3 py-1">
                            <Calendar className="h-3 w-3 mr-1" />
                            {Array.isArray(post.availability) ? post.availability.join(', ') : post.availability}
                        </Badge>
                    )}
                </div>

                {/* Gender Preference & Start Time */}
                <div className="flex items-center gap-4 mb-4 text-sm">
                    {post.gender_preference && post.gender_preference !== 'no_preference' && (
                        <div className="flex items-center text-gray-600">
                            <User className="h-4 w-4 mr-1" />
                            <span>Prefers {post.gender_preference} tutor</span>
                        </div>
                    )}
                    {post.start_time && (
                        <div className="flex items-center text-gray-600">
                            <Target className="h-4 w-4 mr-1" />
                            <span>{post.start_time.replace(/_/g, ' ')}</span>
                        </div>
                    )}
                </div>

                {/* Stats & Action Row */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div className="flex items-center space-x-4">
                        {post.purchase_count > 0 && (
                            <div className="flex items-center space-x-1.5 text-emerald-600">
                                <Users className="h-4 w-4" />
                                <span className="text-sm font-semibold">{post.purchase_count} Contacted</span>
                            </div>
                        )}
                        {post.view_count > 0 && (
                            <div className="flex items-center space-x-1.5 text-gray-500">
                                <Eye className="h-4 w-4" />
                                <span className="text-sm">{post.view_count} views</span>
                            </div>
                        )}
                        {post.applications_count > 0 && (
                            <div className="flex items-center space-x-1.5 text-blue-600">
                                <GraduationCap className="h-4 w-4" />
                                <span className="text-sm font-semibold">{post.applications_count} applications</span>
                            </div>
                        )}
                    </div>

                    <Button
                        onClick={(e) => { e.stopPropagation(); onViewDetails(post.id); }}
                        className="bg-gradient-to-r from-[#041f96] to-[#041f96] hover:from-emerald-700 hover:to-teal-700 text-white px-6 shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 transition-all"
                        data-testid={`view-apply-btn-${post.id}`}
                    >
                        <MousePointerClick className="h-4 w-4 mr-2" />
                        View Details
                    </Button>
                </div>
            </div>
        </CardContent>
    </Card>
);

export default TuitionPostCard;
